import { MiddlewareArray } from '@reduxjs/toolkit'
import { useEffect, useRef, useState } from 'react'
import { Reducer, Store } from 'redux'

import siteBuilderReducer from 'core-components/sitebuilder/store/siteBuilderSlice'
import { createAppStore } from 'src/core/store/createAppStore'
import { rootReducer } from 'src/core/store/reducer'
import Logger from 'src/core/utils/Logger'

import { IAppStore } from '../models/IAppStore'

export const useAppStore = ({
  storeReducer,
  storeMiddleware,
  setApplicationStore,
  remoteReducers,
  devTools,
}: IAppStore) => {
  const [isReducersLoading, setIsLoading] = useState<boolean>(!!remoteReducers)
  const [loadedReducers, setLoadedReducers] = useState<{ [key: string]: Reducer }>({})
  const [loadedMiddlewares] = useState<MiddlewareArray<any>>(new MiddlewareArray())
  const [loadedStoreSetters, setLoadedStoreSetters] = useState<((store: Store) => void)[]>([])

  const storeRef = useRef<Store | null>(null)

  if (!storeRef.current) {
    storeRef.current = createAppStore(
      storeReducer,
      storeMiddleware,
      loadedReducers,
      loadedMiddlewares,
      loadedStoreSetters,
      setApplicationStore,
      devTools
    )
  }

  useEffect(() => {
    if (!isReducersLoading && storeRef.current) {
      storeRef.current.replaceReducer(
        rootReducer({ siteBuilder: siteBuilderReducer, ...storeReducer, ...loadedReducers })
      )
      loadedStoreSetters.forEach((setter) => setter(storeRef.current!))
    }
  }, [isReducersLoading, loadedReducers, loadedStoreSetters, storeReducer])

  useEffect(() => {
    if (!remoteReducers) {
      return
    }

    const reducers = {}
    const storeSetters: ((store: Store) => void)[] = []

    const promises = remoteReducers.map(
      async ({ key, initReducer, initMiddleware, initStoreSetter }) => {
        try {
          const innerPromises = [
            initReducer().then((moduleReducers) => {
              if (moduleReducers) {
                Object.assign(reducers, moduleReducers)
              }
            }),
          ]

          if (initMiddleware) {
            innerPromises.push(
              initMiddleware().then((moduleMiddleware) => {
                if (moduleMiddleware) {
                  loadedMiddlewares.push(...moduleMiddleware)
                }
              })
            )
          }

          if (initStoreSetter) {
            innerPromises.push(
              initStoreSetter().then((storeSetter) => {
                if (storeSetter) {
                  storeSetters.push(storeSetter)
                }
              })
            )
          }

          return Promise.all(innerPromises)
        } catch (e) {
          Logger.error(`Failed to load remote reducer: ${key}`, e)
        }
        return []
      }
    )

    Promise.all(promises).then(() => {
      setLoadedReducers(reducers)
      setLoadedStoreSetters(storeSetters)
      setIsLoading(false)
    })

  }, [remoteReducers])


  return { appStore: storeRef.current, isReducersLoading }
}
